import React, { Fragment, useCallback, useEffect } from 'react'
import { initializeApp } from 'firebase/app'
import { getToken, getMessaging, onMessage } from 'firebase/messaging'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import axiosInterceptorInstance from 'api/client'

import CustomToast from 'ui/CustomToast'

import PropTypes from 'prop-types'
import { handleRequestError } from 'api/handleRequestError'
import { useAuth } from 'features/auth/store/authStore'
import { getOrCreateDeviceId, getBrowserMetadata } from 'utils/device/deviceId'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
}

// Public key; the fallback keeps builds without the env var working.
const VAPID_KEY =
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BMHNmZ22kF9Tccg3Qczbpq1eAD_1jPdullfK-i2_4k_N4nj5cpMTkl6Ao-qyGBAx3Llm27XjyDSl3Z8gs0isXBs'

const NOTIFICATION_ICON = '/revvit-logo-192x192.png'

const firebaseApp = initializeApp(firebaseConfig)
const messaging = getMessaging(firebaseApp)

export const getOrRegisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    // Always register: it is idempotent for the same URL + scope (and checks
    // for an updated script), and it replaces older registrations of
    // /firebase-messaging-sw.js without the config query string, whose
    // worker could not initialise Firebase.
    const searchParams = new URLSearchParams({
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
      messagingSenderId:
        process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
    }).toString()

    return window.navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${searchParams}`,
      {
        scope: '/firebase-push-notification-scope',
      },
    )
  }
  throw new Error('The browser doesn`t support service worker.')
}

export const getFirebaseToken = () =>
  getOrRegisterServiceWorker().then((serviceWorkerRegistration) =>
    getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    }),
  )

/**
 * Register this browser with the multi-device registry. The legacy
 * store/token call is kept so users.notification_token stays current while
 * the backend `multi_device_notifications` flag is off. Only a legacy
 * failure is surfaced; the device registry is best-effort.
 */
export const registerWebDevice = async (fcmToken) => {
  const metadata = getBrowserMetadata()

  const [legacy, device] = await Promise.allSettled([
    axiosInterceptorInstance.post('/push/notification/store/token', {
      token: fcmToken,
    }),
    axiosInterceptorInstance.post('/notifications/devices/register', {
      deviceId: getOrCreateDeviceId(),
      fcmToken,
      platform: 'web',
      appType: 'web',
      appVersion: process.env.REACT_APP_VERSION || null,
      deviceName: metadata.deviceName,
      deviceModel: metadata.deviceModel,
      osVersion: metadata.osVersion,
      permissionStatus:
        'Notification' in window ? Notification.permission : 'default',
    }),
  ])

  if (device.status === 'rejected') {
    console.warn('Web device registration failed:', device.reason)
  }
  if (legacy.status === 'rejected') {
    throw legacy.reason
  }
}

export const NotificationProvider = ({ children }) => {
  const { role: Approle, setRole } = useAuth()

  const navigate = useNavigate()

  const openNotificationTarget = useCallback(
    (data) => {
      const newURL = data?.click_action
      if (!newURL || !newURL.includes(window.location.origin)) return

      const { pathname } = new URL(newURL)
      const goToTarget = () => {
        if (data.collapseKey) {
          navigate(pathname, {
            state: { chatNotification: true, senderId: data.collapseKey },
          })
        } else {
          navigate(pathname)
        }
      }

      const role = data.click_role
      // 'all' (admin broadcasts) targets every role: no switch needed
      if (!role || role === 'all' || role === Approle) {
        goToTarget()
        return
      }

      // Notification targets the other role: switch first, then navigate
      axiosInterceptorInstance
        .put(`/auth/user/update-role`, { role })
        .then((response) => {
          if (response.data.success) {
            setRole(response.data.role)
            goToTarget()
          }
        })
        .catch((error) => {
          handleRequestError(error)
          CustomToast({
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
          })
        })
    },
    [navigate, Approle, setRole],
  )

  const showOsNotification = useCallback(
    (title, body, data = {}) => {
      const options = { body, icon: NOTIFICATION_ICON, data }
      try {
        const notification = new Notification(title, options)
        notification.onclick = (event) => {
          event.preventDefault()
          window.focus()
          openNotificationTarget(data)
          notification.close()
        }
      } catch {
        // Some browsers (e.g. Chrome on Android) only allow notifications
        // from a service worker; its notificationclick handler opens the URL.
        getOrRegisterServiceWorker()
          .then((registration) => registration.showNotification(title, options))
          .catch((error) => console.warn('Notification display failed:', error))
      }
    },
    [openNotificationTarget],
  )

  useEffect(() => {
    // Firebase calls onMessage only while a tab is visible; otherwise the
    // service worker shows the banner. Either way exactly one OS banner is
    // shown per push. The toast is only a fallback when OS notifications
    // aren't available.
    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload.notification?.title || payload.data?.title
      const body = payload.notification?.body || payload.data?.body || ''
      if (!title && !body) return

      if ('Notification' in window && Notification.permission === 'granted') {
        showOsNotification(title || 'Revvit', body, payload.data)
        return
      }

      toast.info(title ? `${title}: ${body}` : body, {
        position: 'top-right',
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
        theme: 'light',
        onClick: () => openNotificationTarget(payload.data),
      })
    })

    return () => {
      unsubscribe()
    }
  }, [openNotificationTarget, showOsNotification])

  return <Fragment>{children}</Fragment>
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
